<template>
  <div class="date-filter__container z-40" v-if="isOpen">
    <button class="date-filter__close" @click="close">
      <vue-feather type="x" size="16" />
    </button>

    <div class="md:w-[70%] mx-auto w-full">
      <div
        class="my-5 font-bold text-xl md:text-center uppercase dark:text-gray-100"
      >
        Filter by date
      </div>

      <div class="date-filter__content">
        <div class="button-chips">
          <button
            class="button-chips__item"
            v-for="interval in getDateRanges"
            :key="interval"
            :class="{
              'button-chips__item--active': interval.time === intervalValue,
            }"
            @click="updateInterval(interval.dateRange)"
          >
            {{ interval.time }}
          </button>
        </div>
        <div class="w-full">
          <div class="grid gap-3 md:grid-cols-2">
            <hs-date-input
              v-model="dateRangeStartInput"
              type="datetime-local"
              label="From"
              placeholder="Start date & time"
              :max="dateRangeEndInput"
            />
            <hs-date-input
              v-model="dateRangeEndInput"
              type="datetime-local"
              label="To"
              placeholder="End date & time"
              :min="dateRangeStartInput"
            />
          </div>
          <hs-button label="Filter" @click="filter" class="mt-5" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import HsDateInput from "@/components/ui/HsDateInput.vue";
export default {
  components: { HsDateInput },
  props: {
    isOpen: Boolean,
    isDark: Boolean,
    initialDateRange: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dateRange: this.initialDateRange || {
        start: new Date(new Date() - new Date(30 * 24 * 60 * 60 * 1000)),
        end: new Date(),
      },
      intervals: [
        "current year",
        "current month",
        "current week",
        "last year",
        "last month",
        "last week",
      ],
      timePhrases: [
        "current year",
        "current month",
        "current week",
        "last year",
        "last month",
        "last week",
      ],
    };
  },
  watch: {
    initialDateRange: {
      handler(newValue) {
        if (newValue) {
          this.dateRange = {
            start: new Date(newValue.start),
            end: new Date(newValue.end),
          };
        }
      },
      deep: true,
      immediate: true,
    },
    dateRange: {
      handler(newValue) {
        if (newValue && newValue.start && newValue.end) {
          const startDate = new Date(newValue.start);
          const endDate = new Date(newValue.end);

          // Check if dates are already in correct order with proper time components
          const isAlreadyCorrect =
            startDate <= endDate &&
            startDate.getHours() === 0 &&
            startDate.getMinutes() === 0 &&
            startDate.getSeconds() === 0 &&
            endDate.getHours() === 23 &&
            endDate.getMinutes() === 59 &&
            endDate.getSeconds() === 59;

          // Only update if not already correct
          if (!isAlreadyCorrect) {
            // Determine which date is earlier and which is later
            const earlierDate = startDate <= endDate ? startDate : endDate;
            const laterDate = startDate <= endDate ? endDate : startDate;

            // Set earlier date to 00:00:00
            earlierDate.setHours(0, 0, 0, 0);

            // Set later date to 23:59:59
            laterDate.setHours(23, 59, 59, 999);

            // Update the dateRange with corrected order and time components
            this.$nextTick(() => {
              this.dateRange = {
                start: earlierDate,
                end: laterDate,
              };
            });
          }
        }
      },
      deep: true,
    },
  },
  computed: {
    isDarkMode() {
      return this.isDark;
    },
    dateRangeStartInput: {
      get() {
        return this.toDateTimeInput(this.dateRange.start);
      },
      set(value) {
        this.dateRange = {
          ...this.dateRange,
          start: value ? new Date(value) : null,
        };
      },
    },
    dateRangeEndInput: {
      get() {
        return this.toDateTimeInput(this.dateRange.end);
      },
      set(value) {
        this.dateRange = {
          ...this.dateRange,
          end: value ? new Date(value) : null,
        };
      },
    },
    getDateRanges() {
      const dateRanges = {
        "current year": () => ({
          time: "current year",
          dateRange: {
            start: new Date(new Date().getFullYear(), 0, 1),
            end: new Date(),
          },
        }),
        "current month": () => ({
          time: "current month",
          dateRange: {
            start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            end: new Date(),
          },
        }),
        "current week": () => {
          const currentDate = new Date();
          const startOfWeek = new Date(
            currentDate - currentDate.getDay() * 24 * 60 * 60 * 1000
          );
          return {
            time: "current week",
            dateRange: { start: startOfWeek, end: currentDate },
          };
        },
        "last year": () => ({
          time: "last year",
          dateRange: {
            start: new Date(new Date().getFullYear() - 1, 0, 1),
            end: new Date(new Date().getFullYear() - 1, 11, 31),
          },
        }),
        "last month": () => {
          const lastMonthDate = new Date();
          lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
          return {
            time: "last month",
            dateRange: {
              start: new Date(
                lastMonthDate.getFullYear(),
                lastMonthDate.getMonth(),
                1
              ),
              end: new Date(
                lastMonthDate.getFullYear(),
                lastMonthDate.getMonth() + 1,
                0
              ),
            },
          };
        },
        "last week": () => {
          const currentDate = new Date();
          const lastWeekStart = new Date(currentDate - 7 * 24 * 60 * 60 * 1000);
          return {
            time: "last week",
            dateRange: { start: lastWeekStart, end: currentDate },
          };
        },
      };

      return this.timePhrases
        .map((phrase) => dateRanges[phrase]())
        .filter(Boolean);
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    filter() {
      this.$emit("filter", this.dateRange);
      this.$emit("close");
      document.body.removeAttribute("style");
    },
    updateInterval(dateRange) {
      this.$emit("filter", dateRange);
      this.$emit("close");
      document.body.removeAttribute("style");
    },
    toDateTimeInput(value) {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return format(date, "yyyy-MM-dd'T'HH:mm");
    },
  },
};
</script>

<style lang="scss">
.date {
  &-filter {
    &__container {
      @apply absolute sm:fixed top-0 left-0 px-8 w-full h-full md:h-[calc(100vh-150px)] overflow-auto dark:bg-slate-800 bg-gray-50 z-10;
      animation: slideInTop 1s forwards;
    }

    &__close {
      @apply absolute top-4 sm:top-6 right-8 w-7 h-7 flex justify-center items-center cursor-pointer border-2 border-transparent border-gray-900 dark:border-gray-300 text-gray-900 dark:text-gray-300 rounded-full;
    }

    &__content {
      @apply flex flex-col gap-6 items-start justify-center sm:mt-10;

      .button-chips {
        @apply flex w-full flex-wrap sm:gap-2 items-start justify-between;

        &__item {
          @apply px-4 py-2 mt-1 w-[160px] sm:w-[152px] rounded-md uppercase text-sm sm:text-[13px] font-semibold border-2 border-gray-800 dark:border-gray-300 dark:text-gray-300 text-gray-800 hover:bg-gray-800 hover:text-gray-50 hover:dark:bg-gray-300 hover:dark:text-gray-800 cursor-pointer;
        }
      }
    }
  }
}

.date-filter__container.closing {
  animation: slideOutBottom 1s forwards;
}

@keyframes slideOutBottom {
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
}

@keyframes slideInTop {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
}

.vc-time-picker.vc-attached {
  width: 50%;

  &:last-child {
    position: absolute;
    right: 0;
    bottom: 0;
  }
}
</style>
